import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, Settings, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/hiring`;

const Configuration = () => {
  const [config, setConfig] = useState(null);
  const [currencyRates, setCurrencyRates] = useState([]);
  const [analysisYear, setAnalysisYear] = useState(2025);
  const [defaultCurrency, setDefaultCurrency] = useState('USD');
  const [isRateDialogOpen, setIsRateDialogOpen] = useState(false);
  const [fetchingRates, setFetchingRates] = useState(false);
  const [isClearDataDialogOpen, setIsClearDataDialogOpen] = useState(false);
  const [clearingData, setClearingData] = useState(false);
  const [rateFormData, setRateFormData] = useState({
    currency: '',
    rate_to_usd: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchConfig();
    fetchCurrencyRates();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      setConfig(response.data);
      setAnalysisYear(response.data.analysis_year);
      setDefaultCurrency(response.data.default_currency || 'USD');
    } catch (error) {
      console.error('Error fetching config:', error);
    }
  };

  const fetchCurrencyRates = async () => {
    try {
      const response = await axios.get(`${API}/currency-rates`);
      setCurrencyRates(response.data);
    } catch (error) {
      console.error('Error fetching currency rates:', error);
    }
  };

  const handleUpdateYear = async () => {
    try {
      await axios.put(`${API}/config?analysis_year=${analysisYear}&default_currency=USD`);
      toast({ title: 'Success', description: 'Configuration updated successfully' });
      fetchConfig();
    } catch (error) {
      console.error('Error updating config:', error);
      toast({ title: 'Error', description: 'Failed to update configuration', variant: 'destructive' });
    }
  };

  const handleFetchRates = async () => {
    setFetchingRates(true);
    try {
      const response = await axios.post(`${API}/fetch-exchange-rates`);
      toast({ title: 'Success', description: `Updated ${response.data.updated_count} currency rates` });
      fetchCurrencyRates();
      setFetchingRates(false);
    } catch (error) {
      console.error('Error fetching rates:', error);
      toast({ title: 'Error', description: 'Failed to fetch live exchange rates', variant: 'destructive' });
      setFetchingRates(false);
    }
  };

  const handleCreateRate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/currency-rates`, rateFormData);
      toast({ title: 'Success', description: 'Currency rate created successfully' });
      setIsRateDialogOpen(false);
      setRateFormData({ currency: '', rate_to_usd: 0 });
      fetchCurrencyRates();
    } catch (error) {
      console.error('Error creating currency rate:', error);
      toast({ title: 'Error', description: 'Failed to create currency rate', variant: 'destructive' });
    }
  };

  const handleDeleteRate = async (currency) => {
    if (!window.confirm('Are you sure you want to delete this currency rate?')) return;
    
    try {
      await axios.delete(`${API}/currency-rates/${currency}`);
      toast({ title: 'Success', description: 'Currency rate deleted successfully' });
      fetchCurrencyRates();
    } catch (error) {
      console.error('Error deleting currency rate:', error);
      toast({ title: 'Error', description: 'Failed to delete currency rate', variant: 'destructive' });
    }
  };

  const handleClearData = async () => {
    setClearingData(true);
    try {
      const response = await axios.delete(`${API}/clear-data`);
      toast({ 
        title: 'Success', 
        description: `All data cleared successfully. Deleted ${response.data.deleted.employees} employees and ${response.data.deleted.pay_raises} pay raises.` 
      });
      setIsClearDataDialogOpen(false);
    } catch (error) {
      console.error('Error clearing data:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to clear data', 
        variant: 'destructive' 
      });
    } finally {
      setClearingData(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card data-testid="configuration-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <CardTitle>System Configuration</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="general" className="w-full">
            <TabsList>
              <TabsTrigger value="general" data-testid="tab-general">General Settings</TabsTrigger>
              <TabsTrigger value="currency" data-testid="tab-currency">Currency Rates</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">General Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="analysis_year">Analysis Year</Label>
                    <div className="flex gap-4 items-end">
                      <Input
                        id="analysis_year"
                        type="number"
                        value={analysisYear}
                        onChange={(e) => setAnalysisYear(parseInt(e.target.value))}
                        className="w-32"
                        data-testid="input-analysis-year"
                      />
                      <Button onClick={handleUpdateYear} data-testid="update-config-button">
                        Save Settings
                      </Button>
                    </div>
                  </div>
                  {config && (
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div>Current analysis year: {config.analysis_year}</div>
                      <div>Default currency: USD</div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="border-red-200">
                <CardHeader className="bg-red-50">
                  <CardTitle className="text-lg text-red-900">Data Management</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-6">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Clear all employee and pay raise data from the database. This action cannot be undone.
                    </p>
                    <Dialog open={isClearDataDialogOpen} onOpenChange={setIsClearDataDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="destructive" data-testid="clear-data-button">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Clear All Data
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Confirm Data Deletion</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <p className="text-sm text-muted-foreground">
                            Are you sure you want to delete all employees and pay raises? This will:
                          </p>
                          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                            <li>Delete all employee records</li>
                            <li>Delete all pay raise records</li>
                            <li>Clear all dashboard data</li>
                          </ul>
                          <p className="text-sm font-semibold text-red-600">
                            This action cannot be undone!
                          </p>
                        </div>
                        <DialogFooter>
                          <Button 
                            variant="outline" 
                            onClick={() => setIsClearDataDialogOpen(false)}
                            disabled={clearingData}
                          >
                            Cancel
                          </Button>
                          <Button 
                            variant="destructive" 
                            onClick={handleClearData}
                            disabled={clearingData}
                            data-testid="confirm-clear-data-button"
                          >
                            {clearingData ? 'Clearing...' : 'Yes, Clear All Data'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="currency" className="space-y-4">
              <div className="flex justify-end gap-3">
                <Button 
                  onClick={handleFetchRates} 
                  variant="outline"
                  disabled={fetchingRates}
                  data-testid="fetch-rates-button"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${fetchingRates ? 'animate-spin' : ''}`} />
                  {fetchingRates ? 'Fetching...' : 'Fetch Live Rates'}
                </Button>
                <Dialog open={isRateDialogOpen} onOpenChange={setIsRateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-currency-rate-button">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Currency Rate
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Currency Rate</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateRate} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="currency">Currency Code (e.g., INR, EUR) *</Label>
                        <Input
                          id="currency"
                          value={rateFormData.currency}
                          onChange={(e) => setRateFormData({ ...rateFormData, currency: e.target.value.toUpperCase() })}
                          required
                          maxLength={3}
                          data-testid="input-currency-code"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rate_to_usd">Rate to USD *</Label>
                        <Input
                          id="rate_to_usd"
                          type="number"
                          step="0.0001"
                          value={rateFormData.rate_to_usd}
                          onChange={(e) => setRateFormData({ ...rateFormData, rate_to_usd: parseFloat(e.target.value) })}
                          required
                          data-testid="input-rate-to-usd"
                        />
                        <div className="text-xs text-muted-foreground">
                          Example: If 1 USD = 83 INR, enter 0.012048 (1/83)
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setIsRateDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" data-testid="submit-currency-rate-button">Create</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Currency Conversion Rates</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Currency</TableHead>
                        <TableHead>Rate to USD</TableHead>
                        <TableHead>Updated Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currencyRates.map((rate) => (
                        <TableRow key={rate.currency} data-testid={`currency-rate-row-${rate.currency}`}>
                          <TableCell className="font-bold">{rate.currency}</TableCell>
                          <TableCell>{rate.rate_to_usd.toFixed(6)}</TableCell>
                          <TableCell>{new Date(rate.updated_date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteRate(rate.currency)}
                              data-testid={`delete-currency-rate-${rate.currency}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Configuration;